import { useState, useEffect } from 'react';
import SoundAvatar from '../Common/SoundAvatar.jsx';
import Footer from '../Common/Footer.jsx';
import formatClipTitle from '../../utils/formatClipTitle.js';
import { useFontScale } from '../../utils/useFontScale.js';
import '../../styles/theme.css';

function pickSignatureSet(eligible, excludeIds = new Set()) {
  const candidates = eligible.filter((c) => !excludeIds.has(c.id));
  const seenCat = new Set();
  const seenLabel = new Set();
  const picks = [];

  for (const c of candidates) {
    if (seenCat.has(c.category) || seenLabel.has(c.label)) continue;
    seenCat.add(c.category);
    seenLabel.add(c.label);
    picks.push(c);
    if (picks.length === 9) break;
  }

  for (const c of candidates) {
    if (picks.length === 9) break;
    if (!picks.includes(c) && !seenLabel.has(c.label)) {
      seenLabel.add(c.label);
      picks.push(c);
    }
  }

  return picks;
}

export function Join({
  initialRoomCode = '',
  initialPlayerName = '',
  onJoin = () => {},
  errorMessage = '',
}) {
  const { scale, increase, decrease } = useFontScale();
  const [roomCode, setRoomCode] = useState(initialRoomCode.toUpperCase());
  const [playerName, setPlayerName] = useState(initialPlayerName);
  const [signatureClips, setSignatureClips] = useState([]);
  const [allEligibleClips, setAllEligibleClips] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState('');
  const [previewingId, setPreviewingId] = useState(null);
  const [hasShuffled, setHasShuffled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/library')
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => {
        if (cancelled || !m?.clips) return;
        const eligible = m.clips.filter(
          (c) => c.avatarCandidate !== false && !c.isVoice && (c.duration || 0) <= 2.0
        );
        setAllEligibleClips(eligible);

        const initialPicks = pickSignatureSet(eligible, new Set());
        setSignatureClips(initialPicks);
        setSelectedAvatar((cur) => cur || initialPicks[0]?.id || '');
      })
      .catch(() => { /* silent fallback */ });
    return () => { cancelled = true; };
  }, []);

  const handleShuffle = () => {
    if (hasShuffled || allEligibleClips.length === 0) return;
    const currentIds = new Set(signatureClips.map((c) => c.id));
    const newPicks = pickSignatureSet(allEligibleClips, currentIds);
    if (newPicks.length > 0) {
      setSignatureClips(newPicks);
      setSelectedAvatar(newPicks[0].id);
      setHasShuffled(true);
    }
  };

  const previewSignature = (clip) => {
    setSelectedAvatar(clip.id);
    const url = clip.formats?.opus || clip.formats?.mp3;
    if (!url) return;
    try {
      const a = new Audio(url);
      a.volume = 0.5;
      setPreviewingId(clip.id);
      a.onended = () => setPreviewingId(null);
      a.play().catch(() => setPreviewingId(null));
    } catch {
      setPreviewingId(null);
    }
  };

  const [turnstileVerified] = useState(true);
  const [validationError, setValidationError] = useState('');

  const handleSubmit = (e) => {
    e?.preventDefault();
    setValidationError('');

    if (!roomCode.trim()) {
      setValidationError('Please enter a room code');
      return;
    }

    if (!playerName.trim()) {
      setValidationError('Please enter a nickname');
      return;
    }

    if (!turnstileVerified) {
      setValidationError('Please complete captcha verification');
      return;
    }

    onJoin({
      roomCode: roomCode.trim().toUpperCase(),
      playerName: playerName.trim(),
      avatar: selectedAvatar,
      captchaToken: 'test-turnstile-token',
    });
  };

  return (
    <div
      data-testid="join-form-card"
      style={{
        width: '100%',
        maxWidth: '390px',
        margin: '0 auto',
        background: '#08090B',
        color: '#EDEFF2',
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {/* Top Header */}
      <div style={{ flex: 'none', padding: '24px 24px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <path d="M2 11h1.6M5.6 6.6v8.8M9.2 2.8v16.4M12.8 7.4v7.2M16.4 9.6v2.8M20 10.4v1.2" stroke="#3ACFCF" strokeWidth="1.9" strokeLinecap="round" />
            </svg>
            <h1 style={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: '-0.02em', color: '#EDEFF2', margin: 0, padding: 0 }}>SoundDrift</h1>
          </div>

          {/* Font Scale Stepper */}
          <div
            data-testid="player-font-scale-adjuster"
            style={{
              display: 'flex',
              alignItems: 'center',
              background: 'var(--sd-panel-2, #0E1116)',
              border: '1px solid var(--sd-line-2, #1C2229)',
              borderRadius: '8px',
              padding: '2px 4px',
              gap: '2px',
            }}
          >
            <button
              type="button"
              data-testid="player-font-decrease-btn"
              onClick={decrease}
              title="Decrease text size"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--sd-ink-2, #C6CDD4)',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
                minWidth: '44px',
                minHeight: '44px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              A-
            </button>
            <span
              style={{
                fontSize: '10.5px',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                color: 'var(--sd-dim, #6E7982)',
                minWidth: '32px',
                textAlign: 'center',
              }}
            >
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              data-testid="player-font-increase-btn"
              onClick={increase}
              title="Increase text size"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--sd-ink-2, #C6CDD4)',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                minWidth: '44px',
                minHeight: '44px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              A+
            </button>
          </div>
        </div>
        <div style={{ marginTop: '20px', fontSize: '1.65rem', fontWeight: 800, lineHeight: 1.14, letterSpacing: '-0.028em', color: '#EDEFF2' }}>
          Pick the sound<br />that means you.
        </div>
        <div style={{ marginTop: '8px', fontSize: '0.88rem', lineHeight: 1.5, color: '#79838D', maxWidth: '30ch' }}>
          It plays when you score, and its shape is your mark all night.
        </div>
      </div>

      {/* Middle Scrollable Section */}
      <div className="sd-scroll" style={{ flex: '1 1 0', overflowY: 'auto', padding: '20px 24px 0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Signature Selector Header */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '-4px' }}>
            <span style={{ fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace', fontSize: '10.5px', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#6E7982' }}>
              Your signature
            </span>
            <button
              type="button"
              data-testid="shuffle-avatar-btn"
              disabled={hasShuffled}
              onClick={handleShuffle}
              style={{
                background: 'transparent',
                border: 'none',
                color: hasShuffled ? '#6E7982' : '#79838D',
                padding: '0 4px',
                minHeight: '44px',
                minWidth: '44px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '7px',
                cursor: hasShuffled ? 'not-allowed' : 'pointer',
                outline: 'none',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M9.6 2.4l2 2-2 2M12 4.4H9.9c-1.6 0-2.4 1-3.2 2.4S5.1 9.6 3.5 9.6H2M2 4.4h1.5c.9 0 1.5.3 2.1 1M9.6 7.6l2 2-2 2M12 9.6h-1.4" stroke={hasShuffled ? '#6E7982' : '#79838D'} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{ fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace', fontSize: '10.5px', letterSpacing: '0.1em', textTransform: 'uppercase', color: hasShuffled ? '#6E7982' : '#79838D' }}>
                {hasShuffled ? 'No more swaps' : 'Show me others'}
              </span>
            </button>
          </div>

          {/* 3x3 Grid of 74px SoundAvatars */}
          <div
            data-testid="avatar-picker"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '9px',
            }}
          >
            {signatureClips.length === 0 ? (
              <span style={{ color: '#6E7982', fontSize: '0.85rem', gridColumn: 'span 3', textAlign: 'center', padding: '24px 0' }}>Loading sounds…</span>
            ) : (
              signatureClips.map((clip) => (
                <SoundAvatar
                  key={clip.id}
                  clip={clip}
                  size="md"
                  selected={selectedAvatar === clip.id}
                  playing={previewingId === clip.id}
                  onClick={() => previewSignature(clip)}
                  title={`${formatClipTitle(clip)} — tap to hear`}
                  showLabel={true}
                  style={{
                    width: '100%',
                    height: '74px',
                    minHeight: '74px',
                    minWidth: '44px',
                    borderRadius: '12px',
                    padding: '6px 8px',
                    boxSizing: 'border-box',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
              ))
            )}
          </div>
        </div>

        {/* Headphones Tip Banner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '12px', background: 'rgba(58,207,207,0.06)', border: '1px solid rgba(58,207,207,0.22)' }}>
          <svg width="17" height="17" viewBox="0 0 18 18" fill="none" style={{ flex: 'none' }} aria-hidden="true">
            <path d="M3 10.5V9a6 6 0 0112 0v1.5" stroke="#3ACFCF" strokeWidth="1.5" strokeLinecap="round" />
            <rect x="1.9" y="10.2" width="3.4" height="5.4" rx="1.7" stroke="#3ACFCF" strokeWidth="1.5" />
            <rect x="12.7" y="10.2" width="3.4" height="5.4" rx="1.7" stroke="#3ACFCF" strokeWidth="1.5" />
          </svg>
          <span style={{ fontSize: '12px', lineHeight: 1.4, color: '#9FB4B8' }}>
            Headphones on for picking. The host screen is the room speaker.
          </span>
        </div>
      </div>

      {/* Bottom Inputs and Submit Form */}
      <form onSubmit={handleSubmit} style={{ flex: 'none', padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: '10px', borderTop: '1px solid #1A1F25' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: '0 0 120px', display: 'flex', flexDirection: 'column' }}>
            <input
              type="text"
              data-testid="room-code-input"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              placeholder="K7QM"
              maxLength={6}
              aria-label="Room Code"
              style={{
                width: '100%',
                height: '54px',
                minHeight: '44px',
                minWidth: '44px',
                borderRadius: '12px',
                border: '1px solid #232A31',
                background: '#0E1116',
                color: '#EDEFF2',
                fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                fontSize: '18px',
                fontWeight: 600,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                padding: '0 12px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column' }}>
            <input
              type="text"
              data-testid="player-name-input"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Your Nickname"
              maxLength={15}
              aria-label="Your Nickname"
              style={{
                width: '100%',
                height: '54px',
                minHeight: '44px',
                minWidth: '44px',
                borderRadius: '12px',
                border: '1px solid #232A31',
                background: '#0E1116',
                color: '#EDEFF2',
                fontSize: '15px',
                fontWeight: 600,
                padding: '0 14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* Turnstile Captcha Container */}
        <div
          data-testid="turnstile-container"
          style={{
            background: '#0E1116',
            border: '1px solid #232A31',
            borderRadius: '12px',
            padding: '8px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '0.8rem',
            color: '#79838D',
            minHeight: '44px',
            minWidth: '44px',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#3ACFCF', fontWeight: 'bold' }}>✓</span>
            <span>Cloudflare Turnstile Verified</span>
          </div>
          <span style={{ fontSize: '0.75rem', color: '#6E7982' }}>Protected</span>
        </div>

        {/* Validation or Error Message */}
        {(validationError || errorMessage) && (
          <div
            data-testid="join-error-msg"
            style={{
              background: 'rgba(228, 115, 138, 0.12)',
              border: '1px solid var(--sd-rose, #E4738A)',
              color: 'var(--sd-rose, #E4738A)',
              padding: '8px 12px',
              borderRadius: '10px',
              fontSize: '0.85rem',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {validationError || errorMessage}
          </div>
        )}

        {/* Join Submit Button */}
        <button
          type="submit"
          data-testid="join-btn"
          onClick={handleSubmit}
          style={{
            width: '100%',
            height: '56px',
            minHeight: '56px',
            minWidth: '44px',
            borderRadius: '14px',
            background: '#3ACFCF',
            border: 'none',
            color: '#08090B',
            fontSize: '16px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 0.2s ease',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        >
          Join the room
        </button>

        {/* Host Link */}
        <div style={{ textAlign: 'center', marginTop: '4px' }}>
          <a
            href="/host"
            data-testid="create-host-link"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '44px',
              minWidth: '44px',
              padding: '0 12px',
              color: '#3ACFCF',
              fontWeight: 600,
              fontSize: '0.85rem',
              textDecoration: 'none',
            }}
          >
            Hosting a party? Start Host TV Screen
          </a>
        </div>

        <Footer />
      </form>
    </div>
  );
}

export default Join;

